import {
    collection,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    Unsubscribe,
    doc
} from 'firebase/firestore';
import { db } from '@/shared/config/firebase';

export interface JpEvent {
    id: string;
    nickname: string;
    parsed: string;
    messageTs: string;
    comment: string;
    uniqueId: string;
    userId: string;
}

export interface SuggestionDoc {
    id: string;
    suggestionsRaw: string;
    createdAt: string;
    reason?: string;
    liveId?: string;
    qualityLabel?: string; // hot, normal, cold, silence, dangerous_drop
    trendLabel?: string; // rising, stable, drop, crash
    audienceLabel?: string; // mostly_new, mixed, mostly_regular
}

export interface SnapshotDoc {
    id: string;
    trend: string;
    topEngaged: FollowerStats[];
    topPresent: FollowerStats[];
    viewerHistory: any[];
    lastMessages: any[];
    lastOrdersContext?: string;
    createdAt: string;
    liveId?: string;
}

export interface FollowerStats {
    nickname: string;
    score: number;
    messages: number;
    presenceMs: number;
    likes: number;
    gifts: number;
    lastSeen: number;
    firstSeen: number;
}

export interface CreatorLiveStatus {
    organisationId: string;
    currentSessionId: string | null;
    state: 'IDLE' | 'RUNNING' | 'ENDED' | 'CRASHED';
    lastUpdated: string | any;
}

export interface LiveSession {
    id: string;
    roomId: string;
    uniqueId: string;
    userId: string;
    liveStartedAt: string; // ISO string or timestamp
    organisationId: string;
    lastUpdated: string;
    isActive: boolean;
    liveEndedAt?: string;
    tiktokUsername?: string;
    state?: 'IDLE' | 'RUNNING' | 'ENDED' | 'CRASHED';
    activeClients?: number;
}

export class LiveAssistantRepository {

    static subscribeToCreatorLiveStatus(organizationId: string, callback: (status: CreatorLiveStatus | null) => void): Unsubscribe {
        console.log('📡 Subscribing to CreatorLiveStatus for organizationId:', organizationId);
        
        // The document ID is the organization ID
        const docRef = doc(db, 'creatorLiveStatus', organizationId);
        
        return onSnapshot(docRef, (docSnap) => {
             if (docSnap.exists()) {
                const data = docSnap.data() as CreatorLiveStatus;
                console.log('✅ CreatorLiveStatus data:', data);
                callback(data);
             } else {
                console.log('⚠️ No CreatorLiveStatus found for', organizationId);
                callback(null);
             }
        }, (error) => {
             console.error('❌ Error subscribing to CreatorLiveStatus:', error);
        });
    }

    static subscribeToLiveSessionById(sessionId: string, callback: (session: LiveSession | null) => void): Unsubscribe {
         console.log('📡 Subscribing to specific LiveSession:', sessionId);
         const docRef = doc(db, 'liveSessions', sessionId);
         
         return onSnapshot(docRef, (docSnap) => {
             if (docSnap.exists()) {
                 const data = { id: docSnap.id, ...docSnap.data() } as LiveSession;
                 console.log('✅ LiveSession data:', data);
                 callback(data);
             } else {
                 console.log('⚠️ LiveSession not found:', sessionId);
                 callback(null);
             }
         }, (error) => {
             console.error('❌ Error subscribing to LiveSession:', error);
         });
    }

    static subscribeToSuggestions(organizationId: string, callback: (suggestion: SuggestionDoc | null) => void): Unsubscribe {
        console.log('📡 Subscribing to suggestions for organizationId:', organizationId);

        const q = query(
            collection(db, 'tiktokPrompterSuggestions'),
            where('organisationId', '==', organizationId),
            orderBy('createdAt', 'desc'),
            limit(1)
        );

        return onSnapshot(q, (snapshot) => {
            console.log('📨 Suggestions snapshot received, empty:', snapshot.empty, 'size:', snapshot.size);
            if (!snapshot.empty) {
                const doc = snapshot.docs[0];
                const data = { id: doc.id, ...doc.data() } as SuggestionDoc;
                console.log('✅ Suggestion data received:', {
                    id: data.id,
                    organisationId: data.organisationId,
                    hasSuggestionsRaw: !!data.suggestionsRaw,
                    createdAt: data.createdAt
                });
                callback(data);
            } else {
                console.log('⚠️ No suggestions found for organisationId:', organizationId);
                callback(null);
            }
        }, (error) => {
            console.error('❌ Error subscribing to suggestions:', error);
            console.error('Error details:', {
                code: error.code,
                message: error.message,
                organizationId
            });
            // En cas d'erreur d'index, essayer sans orderBy
            if (error.code === 'failed-precondition') {
                console.warn('⚠️ Index manquant, tentative sans orderBy...');
                const qFallback = query(
                    collection(db, 'tiktokPrompterSuggestions'),
                    where('organisationId', '==', organizationId),
                    limit(1)
                );
                return onSnapshot(qFallback, (snapshot) => {
                    if (!snapshot.empty) {
                        // Trier manuellement par createdAt
                        const sorted = snapshot.docs.sort((a, b) => {
                            const aTime = a.data().createdAt?.toMillis?.() || 0;
                            const bTime = b.data().createdAt?.toMillis?.() || 0;
                            return bTime - aTime;
                        });
                        const doc = sorted[0];
                        const data = { id: doc.id, ...doc.data() } as SuggestionDoc;
                        console.log('✅ Suggestion data (fallback):', data.id);
                        callback(data);
                    } else {
                        callback(null);
                    }
                });
            }
            callback(null);
        });
    }

    static subscribeToSnapshots(organizationId: string, callback: (snapshot: SnapshotDoc | null) => void): Unsubscribe {
        console.log('📡 Subscribing to snapshots for organizationId:', organizationId);

        const q = query(
            collection(db, 'tiktokPrompterSnapshots'),
            where('organisationId', '==', organizationId),
            orderBy('createdAt', 'desc'),
            limit(1)
        );

        return onSnapshot(q, (snapshot) => {
            console.log('📨 Snapshots snapshot received, empty:', snapshot.empty, 'size:', snapshot.size);
            if (!snapshot.empty) {
                const doc = snapshot.docs[0];
                const data = { id: doc.id, ...doc.data() } as SnapshotDoc;
                console.log('✅ Snapshot data received:', {
                    id: data.id,
                    organisationId: data.organisationId,
                    hasTrend: !!data.trend,
                    createdAt: data.createdAt
                });
                callback(data);
            } else {
                console.log('⚠️ No snapshots found for organisationId:', organizationId);
                callback(null);
            }
        }, (error) => {
            console.error('❌ Error subscribing to snapshots:', error);
            console.error('Error details:', {
                code: error.code,
                message: error.message,
                organizationId
            });
            // En cas d'erreur d'index, essayer sans orderBy
            if (error.code === 'failed-precondition') {
                console.warn('⚠️ Index manquant, tentative sans orderBy...');
                const qFallback = query(
                    collection(db, 'tiktokPrompterSnapshots'),
                    where('organisationId', '==', organizationId),
                    limit(1)
                );
                return onSnapshot(qFallback, (snapshot) => {
                    if (!snapshot.empty) {
                        // Trier manuellement par createdAt
                        const sorted = snapshot.docs.sort((a, b) => {
                            const aTime = a.data().createdAt?.toMillis?.() || 0;
                            const bTime = b.data().createdAt?.toMillis?.() || 0;
                            return bTime - aTime;
                        });
                        const doc = sorted[0];
                        const data = { id: doc.id, ...doc.data() } as SnapshotDoc;
                        console.log('✅ Snapshot data (fallback):', data.id);
                        callback(data);
                    } else {
                        callback(null);
                    }
                });
            }
            callback(null);
        });
    }

    static subscribeToJpEvents(organizationId: string, callback: (events: JpEvent[]) => void): Unsubscribe {
        console.log('📡 Subscribing to JP events for organizationId:', organizationId);

        // Get events from the last 10 minutes (or just last 20 events)
        const q = query(
            collection(db, 'tiktokPrompterJpEvents'),
            where('organisationId', '==', organizationId),
            orderBy('messageTs', 'desc'),
            limit(20)
        );

        return onSnapshot(q, (snapshot) => {
            console.log('📨 JP events snapshot received, empty:', snapshot.empty, 'size:', snapshot.size);
            const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as JpEvent));
            console.log('✅ JP events:', events.length, 'events');
            callback(events);
        }, (error) => {
            console.error('❌ Error subscribing to JP events:', error);
        });
    }

    static subscribeToLiveSession(organizationId: string, callback: (session: LiveSession | null) => void, tiktokUsername?: string): Unsubscribe {
        console.log('📡 Subscribing to live session for organizationId:', organizationId);

        const base = [
            where('organisationId', '==', organizationId),
            where('isActive', '==', true),
            limit(1)
        ];

        const q = tiktokUsername
            ? query(collection(db, 'liveSessions'), where('tiktokUsername', '==', tiktokUsername), ...base)
            : query(collection(db, 'liveSessions'), ...base);

        return onSnapshot(q, (snapshot) => {
            console.log('📨 Live session snapshot received, empty:', snapshot.empty);
            if (!snapshot.empty) {
                const doc = snapshot.docs[0];
                const data = { id: doc.id, ...doc.data() } as LiveSession;
                console.log('✅ Live session data:', data);
                callback(data);
            } else {
                console.log('⚠️ No live session found');
                callback(null);
            }
        }, (error) => {
            console.error('❌ Error subscribing to live session:', error);
        });
    }
}
